import { useCallback, useState } from 'react'

interface useLocalStorageStateProps<T> {
  /** The local storage key */
  key: string
  /** The initial value. */
  initialValue: T
  /** If true, the value will not be saved in local storage */
  disableSave?: boolean
}

/** useState that gets and sets it's values from and to local storage. */
export const useLocalStorageState = <T>({ key, initialValue, disableSave }: useLocalStorageStateProps<T>): [T, (newValue: T) => void] => {
  const [value, setValue] = useState(() => initialValue)

  const updateValue = useCallback(
    (newValue: T): void => {
      setValue(newValue)
      if (disableSave) return
      saveToLocalStorage(key, newValue)
    },
    [key, disableSave]
  )

  return [value, updateValue]
}

export const getLocalStorageValue = <T>(key: string): T | null => {
  const storageValue = localStorage.getItem(key)
  if (!storageValue) return null

  try {
    return JSON.parse(storageValue) as T
  } catch (error) {
    console.error(`Failed to parse localStorage value for key "${key}":`, error)
    return null
  }
}

export const saveToLocalStorage = (key: string, value: unknown): void => {
  localStorage.setItem(key, JSON.stringify(value))
}
